/**
 * Admin Results tab for Hide and Seek Teams games.
 *
 * Renders:
 *   - The team standings (each team's total is the sum of its round scores)
 *     and the best hiders, from the standings on every game snapshot
 *   - Per-round breakdowns (newest first): the hiding team, the round's
 *     scoring and what each team scored, and each player's team, outcome and
 *     time, from the admin Results snapshot
 *   - The audit log (right column) from the same snapshot
 */

import { useEffect } from 'react';
import {
  getCheatTypeDisplayText,
  HNST_TEAMS,
  MEDALS,
  otherHnstTeam,
  type AdminHnstResultsPlayer,
  type AdminHnstResultsRound,
  type CheatType,
  type HnstTeamKey,
} from '@swtorbit/shared-socket-events';
import { competitionRanks, formatDuration, formatHnstRoundScoring, formatHnstScore } from '@swtorbit/shared-react-hooks';
import { statusBadgeClass, leaderboardRowClass, HnstTeamName, HnsPathwaysSection } from '@swtorbit/shared-components';
import { useGameContext } from '../../contexts/GameContext';
import { useHnstContext } from '../../contexts/HnstContext';
import { ResultsTabBase } from '../results';
import { HnsResultsAuditLog } from '../hns/HnsResultsAuditLog';
import { hnstUnevenHidingWarning } from '../../utils/hnstUnevenHiding';

function outcomeText(player: AdminHnstResultsPlayer): string {
  switch (player.outcome) {
    case 'seeker':
      return '🔍 Seeker';
    case 'disqualified':
      return `🚫 DQ — ${player.disqualificationReason
        ? getCheatTypeDisplayText(player.disqualificationReason as CheatType)
        : 'cheating'}`;
    case 'captured':
      if (player.capturedByName) return `🎯 Caught by ${player.capturedByName}`;
      return player.eliminatedByDeaths ? '💀 Eliminated (died too many times)' : '🚪 Out (dropped out)';
    case 'escaped':
      return '🙈 Never caught';
    case 'hiding':
      return '🙈 Hiding';
  }
}

/** What each team scored in a completed round, by its scoring. */
function RoundScore({ round, hidingTeam }: { round: AdminHnstResultsRound; hidingTeam: HnstTeamKey }) {
  const hiders = <span className="text-slate fs-sm"> over {round.teamSize} hider{round.teamSize === 1 ? '' : 's'}</span>;
  if (round.scoring.mode === 'mean') {
    return <span>Team average: <strong>{formatHnstScore(round.teamAverageSeconds)}</strong>{hiders}</span>;
  }
  const seekingTeam = otherHnstTeam(hidingTeam);
  const perFind = formatDuration(round.scoring.secondsPerFind);
  return (
    <>
      <span>
        <HnstTeamName team={seekingTeam} /> found {round.found} × {perFind} = <strong>{formatHnstScore(round.teamSeconds[seekingTeam])}</strong>
      </span>
      <span>
        <HnstTeamName team={hidingTeam} />
        {round.scoring.survivorsScore
          ? <> {round.survived} survived × {perFind} = <strong>{formatHnstScore(round.teamSeconds[hidingTeam])}</strong></>
          : <span className="text-slate"> no score this round</span>}
        {hiders}
      </span>
    </>
  );
}

export function HnstResultsTab() {
  const { selectedGame } = useGameContext();
  const { hnstAdminResults, refreshHnstAdminResults } = useHnstContext();

  const selectedDbId = selectedGame?.dbId;
  useEffect(() => {
    if (selectedDbId != null) refreshHnstAdminResults();
  }, [selectedDbId, refreshHnstAdminResults]);

  const standings = selectedGame?.hnstStandings;
  const hasScores = !!standings && standings.totalRounds > 0;
  const ranks = competitionRanks(standings?.players.map((p) => p.totalSeconds) ?? []);
  const isCompleted = selectedGame?.status === 'completed';
  // Abandoned: the standings are final, but no winner is declared.
  const isAbandoned = selectedGame?.status === 'abandoned';
  const unevenHiding = selectedGame ? hnstUnevenHidingWarning(selectedGame) : null;

  return (
    <ResultsTabBase
      hasSelectedGame={!!selectedGame}
      game={selectedGame}
      title="🐸 Results"
      onRefresh={refreshHnstAdminResults}
      lastRefreshedUtcMs={hnstAdminResults?.generatedAtUtcMs}
    >
      {hasScores && standings && (
        <div
          className={`p-lg border-md rounded-base${
            isCompleted ? ' border-gold glow glow-gold' : isAbandoned ? ' border-amber glow glow-amber' : ' border-blue-light glow glow-blue-light'
          }`}
        >
          <div className="d-flex align-items-center justify-content-center mb-sm gap-sm">
            {isCompleted && <span className="fs-lg">🏆</span>}
            <h3 className={isCompleted ? 'text-gold' : isAbandoned ? 'text-amber' : 'text-white'}>
              {isCompleted
                ? standings.leader ? <><HnstTeamName team={standings.leader} /> win!</> : "It's a draw!"
                : isAbandoned
                  ? 'Final standings (game abandoned)'
                  : 'Current Standings'}
            </h3>
          </div>
          <div className="d-flex flex-column gap-xs">
            {standings.teams.map((team, idx) => (
              <div key={team.team} className={leaderboardRowClass(idx)}>
                <span className="leaderboard__rank">{idx + 1}.</span>
                <span className="leaderboard__name"><HnstTeamName team={team.team} /></span>
                <span className="leaderboard__stats">Hid ×{team.roundsHidden} · sum of round scores</span>
                <span className="leaderboard__score">{formatHnstScore(team.totalSeconds)}</span>
              </div>
            ))}
          </div>

          {standings.players.length > 0 && (
            <>
              <h4 className="mt-sm text-white">Best Hiders</h4>
              <div className="d-flex flex-column gap-2xs">
                {standings.players.map((player, idx) => (
                  <div key={player.playerId} className={leaderboardRowClass(ranks[idx] - 1)}>
                    <span className="leaderboard__rank">{ranks[idx] <= 3 ? MEDALS[ranks[idx] - 1] : `${ranks[idx]}.`}</span>
                    <span className="leaderboard__name">
                      {player.characterName}
                      {player.team && <span className={`text-${HNST_TEAMS[player.team].hue}`}> · {HNST_TEAMS[player.team].name}</span>}
                    </span>
                    <span className="leaderboard__stats">
                      Hid ×{player.roundsHidden}
                      {player.timesCaught > 0 && ` · Caught ×${player.timesCaught}`}
                      {player.timesEliminated > 0 && ` · Eliminated ×${player.timesEliminated}`}
                      {player.timesDroppedOut > 0 && ` · Out ×${player.timesDroppedOut}`}
                      {player.timesDisqualified > 0 && ` · DQ ×${player.timesDisqualified}`}
                    </span>
                    <span className="leaderboard__score">{formatHnstScore(player.totalSeconds)}</span>
                  </div>
                ))}
              </div>
            </>
          )}
          {unevenHiding && <p className="alert alert--sm fs-sm text-amber mt-sm">⚠ {unevenHiding}</p>}
          <div className="leaderboard__footer">
            <p>Total Rounds: {standings.totalRounds}</p>
          </div>
        </div>
      )}

      {!hnstAdminResults ? (
        <div className="bg-space bg-o-50 text-cyan p-sm rounded-base border-md border-blue-light">
          Loading round details…
        </div>
      ) : (
        <div className="d-flex flex-column flex-lg-row gap-sm">
          <div className="d-flex flex-column gap-sm flex-1">
            {hnstAdminResults.rounds.length === 0 ? (
              <div className="bg-space bg-o-50 text-cyan p-sm rounded-base border-md border-blue-light">
                No rounds have been created yet.
              </div>
            ) : (
              hnstAdminResults.rounds.map((round) => (
                <div
                  key={round.roundId}
                  className={`bg-space bg-o-50 text-white border-md border-blue-light rounded-base p-sm d-flex flex-column gap-xs${round.disabled ? ' o-50' : ''}`}
                >
                  <div className="d-flex justify-content-between align-items-center gap-xs flex-wrap">
                    <h4 className="text-cyan">
                      Round {round.roundNumber}
                      {round.hidingTeam && <> — <HnstTeamName team={round.hidingTeam} /> hid</>}
                    </h4>
                    <span className={statusBadgeClass(round.status)}>
                      {round.status.toUpperCase()}
                      {round.disabled && ' (DISABLED)'}
                    </span>
                  </div>

                  <span className="text-slate fs-sm">Scoring: {formatHnstRoundScoring(round.scoring)}</span>
                  {round.hidingTeam && round.status === 'completed' && <RoundScore round={round} hidingTeam={round.hidingTeam} />}
                  {!round.hidingTeam && (
                    <span className="text-slate fs-sm">Not started yet: the hiding team and players are decided at start.</span>
                  )}

                  {round.players.length > 0 && (
                    <div className="d-flex flex-column gap-2xs">
                      {round.players.map((player) => (
                        <div key={player.playerId} className="d-flex justify-content-between gap-xs align-items-center">
                          <span className={`text-${HNST_TEAMS[player.team].hue}`}>{player.characterName}</span>
                          <span className="text-cyan fs-sm">
                            {outcomeText(player)}
                            {player.penaltySeconds > 0 && ` · −${player.penaltySeconds}s penalty`}
                            {player.originalRole !== 'seeker' && player.outcome !== 'hiding' &&
                              ` · ${formatHnstScore(player.scoreSeconds)}`}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>

          <div className="flex-1">
            <HnsResultsAuditLog results={hnstAdminResults} />
          </div>
        </div>
      )}

      {/* Each player's route through a finished round (Krall's Accord). */}
      {selectedGame && (
        <div className="bg-space bg-o-50 text-white border-md border-blue-light rounded-base p-sm d-flex flex-column gap-xs">
          <h4 className="text-cyan">Pathways</h4>
          <HnsPathwaysSection game={selectedGame} gameId={selectedGame.dbId} />
        </div>
      )}
    </ResultsTabBase>
  );
}
